import seedrandom from 'seedrandom';
import { Playlists, Track, Tracks } from '../../../@resources/sounds/music';
import { Arr, shuffle as arrayShuffle, memoAll } from '../../../CodesForFoundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { Attribute, component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { ServerDate } from '../../../platform/ServerDate';
import { Rect } from '../../../platform/geom/Rect';
import { FixedRectangular } from '../../canvas/entities/FixedRectangular';
import { Physical, PotentialInteraction, TransformHints } from '../../canvas/entities/Physical';
import { Snappable } from '../../canvas/entities/Snappable';
import { jukeboxLoop, jukeboxNext, jukeboxPlayPause, jukeboxPrev, jukeboxShowTracks, jukeboxShuffle } from './JukeboxCommands';
import { JukeboxLayout } from './JukeboxLayout';
import { jukeboxPropertyLayout } from './JukeboxPropertyLayout';

const invocation = defineCommand(
    'jukeboxInteraction',
    null,
    (cc, [id, region]: [string, string?]) => {
        if (region === 'playPause') {
            cc.dispatch(jukeboxPlayPause, [id]);
        }
        else if (region === 'next') {
            cc.dispatch(jukeboxNext, [id]);
        }
        else if (region === 'prev') {
            cc.dispatch(jukeboxPrev, [id]);
        }
        else if (region === 'shuffle') {
            cc.dispatch(jukeboxShuffle, [id]);
        }
        else if (region === 'loop') {
            cc.dispatch(jukeboxLoop, [id]);
        }
        else if (region === 'screen') {
            cc.dispatch(jukeboxShowTracks, [id]);
        }
    },
    [],
);

const jukeboxDef = {
    name: 'Jukebox',
    implements: [Physical, Snappable],
    invocation,
    propertyLayout: jukeboxPropertyLayout,
};

export const JukeboxProgressAttr = new Attribute<number, Jukebox>('progress', null);

@component(jukeboxDef)
export class Jukebox extends FixedRectangular implements Snappable {

    @property({ type: 'number', default: 220 })
    public width!: number;

    @property({ type: 'number', default: 165 })
    public height!: number;

    @property({ type: 'string', default: '#00877a' })
    public color!: string;

    @property({ type: 'string' })
    public url!: string;

    @property({ type: 'number', default: -1 })
    public pausedAt!: number;

    @property({ type: 'string', default: 'chill' })
    public playlist!: string;

    @property({ type: 'string', default: Tracks[0].id })
    public playingFrom!: string;

    @property({ type: 'number', default: -1 })
    public playingSince!: number;

    @property({ type: 'boolean', default: false })
    public shuffle!: boolean;

    @property({ type: 'string', default: '0' })
    public seed!: string;

    @property({ type: 'boolean', default: false })
    public loop!: boolean;

    public get playing() {
        return this.playingSince >= 0;
    }

    @memoUsing('width', 'height')
    public get layout(): JukeboxLayout {
        return JukeboxLayout(this);
    }

    public get boundary() {
        return true;
    }

    public get shouldSnap() {
        return true;
    }

    @memoAll()
    public queryTransformHints(): TransformHints {
        return {
            scaleX: false,
            scaleY: false,
            rotate: false,
            translate: true,
            visible: true,
        };
    }

    public testInteraction({ type, mpt }: PotentialInteraction, _force?: boolean, _selected?: boolean): [boolean, string] {
        const { screen, playPause, next, prev, shuffle, loop } = this.layout;
        const rpt = this.toRelative(mpt);
        if (this.localBounds.contains(rpt)) {
            if (type === 'invoke') {
                if (playPause.contains(rpt)) {
                    if (rpt.distanceTo(playPause.center()) < playPause.width / 2) {
                        return [true, 'playPause'];
                    }
                }
                else if (next.contains(rpt)) {
                    return [true, 'next'];
                }
                else if (prev.contains(rpt)) {
                    return [true, 'prev'];
                }
                else if (shuffle.contains(rpt)) {
                    return [true, 'shuffle'];
                }
                else if (loop.contains(rpt)) {
                    return [true, 'loop'];
                }
                else if (screen.contains(rpt)) {
                    return [true, 'screen'];
                }
            }
            else {
                return [true, 'default'];
            }
        }
        return [false, 'none'];
    }

    protected setTransformationResult(x: number, y: number, r: number, w: number, h: number): void {
        const newBounds = new Rect(x, y, w, h);
        const newCenter = newBounds.center();

        w = this.width;
        h = this.height;
        x = newCenter.x - (w / 2);
        y = newCenter.y - (h / 2);
        r = 0;

        super.setTransformationResult(x, y, r, w, h);
    }

    public getTrackList() {

        const playlist = Playlists.find(x => x.id === this.playlist);
        if (!playlist) return [];

        if (this.loop) {
            return playlist.tracks.filter(x => x.id === this.playingFrom);
        }
        else if (this.shuffle) {
            return arrayShuffle(Array.from(playlist.tracks), seedrandom(this.seed));
        }
        else {
            return playlist.tracks;
        }
    }

    public getCurrentTrack(): { currentTrack: Track, elapsed: number } {
        const tracks = this.getTrackList();
        let currentTrack = tracks.findIndex(x => x.id === this.playingFrom);

        if (!this.playing) {
            return {
                currentTrack: tracks[currentTrack],
                elapsed: this.pausedAt,
            };
        }

        const trackLength = tracks[currentTrack].length;

        const now = ServerDate.now();
        let elapsed = now - this.playingSince;

        if (elapsed < trackLength) {
            return {
                currentTrack: tracks[currentTrack],
                elapsed,
            };
        }
        else {
            elapsed = elapsed - trackLength;
            currentTrack = (currentTrack + 1) % tracks.length;
            while (elapsed > tracks[currentTrack].length) {
                elapsed = elapsed - tracks[currentTrack].length;
                currentTrack = (currentTrack + 1) % tracks.length;
            }
            return {
                currentTrack: tracks[currentTrack],
                elapsed
            };
        }
    }

    public getNextTrack() {
        const trackList = this.getTrackList();
        const currentIndex = trackList.findIndex(x => x.id === this.playingFrom);
        return Arr.at(trackList, currentIndex + 1);
    }
}
